import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { Camera, X, Loader2, ScanLine, TypeIcon as ManualIcon, Save, Package, CheckCheck, PlusCircle, Store as StoreIcon, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { lookupByGtin, adoptMasterWithPrices, createProduct, formatMXN } from "@/lib/api";

/**
 * Camera-based barcode scanner (@zxing/browser). Supports EAN-13, EAN-8, UPC-A, UPC-E,
 * Code 128 y QR. On scan:
 *  - status='in_store'   → tarjeta con stock y precio actuales
 *  - status='in_catalog' → prefill de captura con brand/name/category del maestro, solo pide precio + stock
 *  - status='unknown'    → captura manual desde cero
 *
 * Props:
 *  - open (bool)
 *  - onClose()
 *  - onSaved()  → llamado tras cada guardar exitoso (create/adopt)
 */
export default function BarcodeScanModal({ open, onClose, onSaved }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const [phase, setPhase] = useState("scanning"); // scanning | looking-up | result | manual-gtin
  const [gtin, setGtin] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [lookupData, setLookupData] = useState(null);
  const [error, setError] = useState("");

  // Adoption / creation form state
  const [salePrice, setSalePrice] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [stock, setStock] = useState("");
  const [manualName, setManualName] = useState("");
  const [manualPresentation, setManualPresentation] = useState("");
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setSalePrice(""); setCostPrice(""); setStock("");
    setManualName(""); setManualPresentation("");
  };

  const stopCamera = () => {
    try { controlsRef.current?.stop(); } catch { /* noop */ }
    controlsRef.current = null;
    readerRef.current = null;
  };

  const startCamera = async () => {
    setError(""); setPhase("scanning");
    setGtin(""); setLookupData(null); resetForm();
    try {
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128, BarcodeFormat.QR_CODE,
      ]);
      hints.set(DecodeHintType.TRY_HARDER, true);
      const reader = new BrowserMultiFormatReader(hints);
      readerRef.current = reader;
      const devices = await BrowserMultiFormatReader.listVideoInputDevices();
      const rearDevice = devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1] || devices[0];
      controlsRef.current = await reader.decodeFromVideoDevice(
        rearDevice?.deviceId,
        videoRef.current,
        (result, err) => {
          if (result && phase === "scanning") {
            handleDecoded(result.getText());
          }
        },
      );
    } catch (e) {
      setError(e?.message || "No pudimos acceder a la cámara. Da permisos o escribe el código a mano.");
    }
  };

  useEffect(() => {
    if (open) startCamera();
    return () => { stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDecoded = async (code) => {
    const clean = (code || "").replace(/\D/g, "");
    if (!clean || clean.length < 6) return;
    stopCamera();
    setGtin(clean);
    setPhase("looking-up");
    try {
      const r = await lookupByGtin(clean);
      setLookupData(r);
      setPhase("result");
      // Prefill form based on status
      if (r.status === "in_catalog" && r.master) {
        setManualName([r.master.brand, r.master.product_name, r.master.variant].filter(Boolean).join(" "));
        setManualPresentation([r.master.net_content_ml ? `${r.master.net_content_ml} ${r.master.unit || "ml"}` : "", r.master.package_type].filter(Boolean).join(" · "));
      }
      if (r.status === "in_external" && r.external) {
        setManualName([r.external.brand, r.external.product_name].filter(Boolean).join(" "));
        setManualPresentation(r.external.quantity || "");
      }
      if (r.status === "unknown") {
        setManualName(""); setManualPresentation("");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Error consultando el código");
      setPhase("manual-gtin");
    }
  };

  const submitManualCode = () => {
    const c = manualCode.replace(/\D/g, "");
    if (!c || c.length < 6) { toast.error("Escribe al menos 6 dígitos"); return; }
    handleDecoded(c);
  };

  const saveInCatalog = async () => {
    const p = parseFloat(salePrice);
    if (!p || p <= 0) { toast.error("Precio de venta requerido"); return; }
    setSaving(true);
    try {
      await adoptMasterWithPrices([{
        master_product_id: lookupData.master.id,
        sale_price: p,
        cost_price: parseFloat(costPrice) || 0,
        stock_quantity: parseFloat(stock) || 0,
      }]);
      toast.success(`${manualName} agregado a tu inventario`);
      onSaved?.(); close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const saveUnknown = async () => {
    const n = manualName.trim();
    const pres = manualPresentation.trim();
    const p = parseFloat(salePrice);
    if (!n) { toast.error("Nombre requerido"); return; }
    if (!p || p <= 0) { toast.error("Precio de venta requerido"); return; }
    setSaving(true);
    try {
      const finalName = pres ? `${n} · ${pres}` : n;
      await createProduct({
        name: finalName,
        category: "Otros",
        sale_price: p,
        cost_price: parseFloat(costPrice) || 0,
        stock_quantity: parseFloat(stock) || 0,
        min_stock_alert: 0,
        unit: "pieza",
        barcode: gtin,
      });
      toast.success(`${finalName} agregado con GTIN ${gtin}`);
      onSaved?.(); close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const close = () => { stopCamera(); onClose?.(); };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-0 sm:p-4"
        onClick={close}
        data-testid="scan-modal"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-md h-full sm:h-auto sm:max-h-[92vh] overflow-y-auto bg-[#0a0a0a] sm:rounded-3xl border border-white/10 flex flex-col"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <ScanLine size={16} className="text-[#CCFF00]" />
              <span className="font-mono text-[10px] tracking-widest text-white/60 uppercase">Escanear código</span>
            </div>
            <button onClick={close} className="text-white/40 hover:text-white" data-testid="scan-close"><X size={18} /></button>
          </div>

          {/* Scanner viewport */}
          {phase === "scanning" && (
            <div className="relative bg-black flex-1 min-h-[320px]" data-testid="scan-viewport">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay muted playsInline
              />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="w-4/5 aspect-[3/1] border-2 border-[#CCFF00] rounded-2xl relative overflow-hidden">
                  <motion.div
                    initial={{ y: 0 }} animate={{ y: [0, 60, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-x-0 h-0.5 bg-[#CCFF00]/80 shadow-[0_0_20px_#CCFF00]"
                  />
                </div>
              </div>
              <div className="absolute bottom-4 left-0 right-0 text-center px-4">
                <p className="text-white/80 text-sm">Enfoca el código de barras del producto</p>
                <button
                  onClick={() => { stopCamera(); setPhase("manual-gtin"); }}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 text-xs font-mono uppercase tracking-widest"
                  data-testid="scan-manual-btn"
                >
                  <ManualIcon size={12} /> Escribir código a mano
                </button>
              </div>
              {error && (
                <div className="absolute top-3 left-3 right-3 rounded-xl bg-red-500/20 border border-red-500/50 p-3 text-red-200 text-xs">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Manual GTIN input */}
          {phase === "manual-gtin" && (
            <div className="p-6" data-testid="scan-manual-input">
              <h3 className="font-display text-2xl tracking-tighter mb-1">Escribe el código</h3>
              <p className="text-white/50 text-sm mb-4">GTIN / EAN-13 (los dígitos abajo del código)</p>
              <input
                autoFocus
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.replace(/\D/g, "").slice(0, 14))}
                onKeyDown={(e) => e.key === "Enter" && submitManualCode()}
                placeholder="7501055310739"
                inputMode="numeric"
                className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white text-lg font-mono tracking-widest focus:border-[#CCFF00] focus:outline-none"
                data-testid="scan-manual-input-field"
              />
              <div className="flex gap-2 mt-4">
                <button onClick={startCamera}
                  className="flex-1 py-3 rounded-full border border-white/10 hover:border-white/30 text-white/70 hover:text-white text-xs font-mono uppercase tracking-widest transition"
                  data-testid="scan-camera-again">
                  <Camera size={12} className="inline mr-1.5" /> Volver a cámara
                </button>
                <button onClick={submitManualCode}
                  className="flex-1 py-3 rounded-full bg-[#CCFF00] text-black text-xs font-mono uppercase tracking-widest hover:bg-white transition"
                  data-testid="scan-manual-submit">
                  <ChevronRight size={12} className="inline mr-1.5" /> Buscar
                </button>
              </div>
            </div>
          )}

          {phase === "looking-up" && (
            <div className="p-10 text-center" data-testid="scan-loading">
              <Loader2 size={32} className="animate-spin mx-auto mb-3 text-[#CCFF00]" />
              <p className="text-white/60 text-sm">Buscando <span className="font-mono">{gtin}</span>…</p>
            </div>
          )}

          {phase === "result" && lookupData && (
            <div className="p-6" data-testid="scan-result">
              <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-2">GTIN {gtin}</p>

              {/* IN STORE */}
              {lookupData.status === "in_store" && (
                <div data-testid="scan-in-store">
                  <div className="rounded-2xl border border-[#CCFF00]/30 bg-[#CCFF00]/[0.05] p-5 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <StoreIcon size={14} className="text-[#CCFF00]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#CCFF00] uppercase">Ya lo vendes</span>
                    </div>
                    <h3 className="font-display text-2xl tracking-tighter mb-3">{lookupData.product.name}</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Precio</p>
                        <p className="font-satoshi text-2xl text-white">{formatMXN(lookupData.product.sale_price)}</p>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Stock</p>
                        <p className="font-satoshi text-2xl text-white">{lookupData.product.stock_quantity}</p>
                      </div>
                    </div>
                  </div>
                  <button onClick={startCamera}
                    className="w-full py-3 rounded-full bg-[#CCFF00] text-black text-xs font-mono uppercase tracking-widest hover:bg-white transition"
                    data-testid="scan-again">
                    <ScanLine size={12} className="inline mr-1.5" /> Escanear otro
                  </button>
                </div>
              )}

              {/* IN CATALOG — prefilled form */}
              {lookupData.status === "in_catalog" && (
                <div data-testid="scan-in-catalog">
                  <div className="rounded-2xl border border-[#CCFF00]/30 bg-[#CCFF00]/[0.05] p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package size={14} className="text-[#CCFF00]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#CCFF00] uppercase">Encontrado en catálogo</span>
                    </div>
                    <p className="text-white text-base">{manualName}</p>
                    <p className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">{manualPresentation || "—"}</p>
                  </div>
                  <PriceStockFields
                    salePrice={salePrice} setSalePrice={setSalePrice}
                    costPrice={costPrice} setCostPrice={setCostPrice}
                    stock={stock} setStock={setStock}
                  />
                  <button onClick={saveInCatalog} disabled={saving || !salePrice}
                    className="mt-4 w-full py-4 rounded-full bg-[#CCFF00] text-black text-xs font-mono uppercase tracking-widest hover:bg-white transition disabled:opacity-40 flex items-center justify-center gap-2"
                    data-testid="scan-save-catalog">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Agregar a mi inventario
                  </button>
                </div>
              )}

              {/* IN EXTERNAL — Open Food Facts fallback */}
              {lookupData.status === "in_external" && (
                <div data-testid="scan-in-external">
                  <div className="rounded-2xl border border-[#00D4FF]/30 bg-[#00D4FF]/[0.06] p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package size={14} className="text-[#00D4FF]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#00D4FF] uppercase">Encontrado en Open Food Facts</span>
                    </div>
                    <div className="flex gap-3 items-start">
                      {lookupData.external.image_url && (
                        <img src={lookupData.external.image_url} alt="" className="w-16 h-16 rounded-lg object-cover bg-white/5 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-white text-base">{manualName}</p>
                        <p className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">
                          {lookupData.external.quantity || "—"}
                          {lookupData.external.category ? ` · ${lookupData.external.category}` : ""}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Nombre <span className="text-white/30 lowercase tracking-normal">(editable)</span></label>
                      <input value={manualName} onChange={(e) => setManualName(e.target.value)}
                        className="w-full mt-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#00D4FF] focus:outline-none"
                        data-testid="scan-external-name" />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Presentación</label>
                      <input value={manualPresentation} onChange={(e) => setManualPresentation(e.target.value)}
                        className="w-full mt-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#00D4FF] focus:outline-none"
                        data-testid="scan-external-presentation" />
                    </div>
                    <PriceStockFields
                      salePrice={salePrice} setSalePrice={setSalePrice}
                      costPrice={costPrice} setCostPrice={setCostPrice}
                      stock={stock} setStock={setStock}
                    />
                  </div>
                  <button onClick={saveUnknown} disabled={saving || !manualName.trim() || !salePrice}
                    className="w-full py-4 rounded-full bg-[#00D4FF] text-black text-xs font-mono uppercase tracking-widest hover:bg-white transition disabled:opacity-40 flex items-center justify-center gap-2"
                    data-testid="scan-save-external">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Agregar a mi inventario
                  </button>
                </div>
              )}

              {/* UNKNOWN — blank form */}
              {lookupData.status === "unknown" && (
                <div data-testid="scan-unknown">
                  <div className="rounded-2xl border border-[#FF5A00]/30 bg-[#FF5A00]/[0.05] p-4 mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <PlusCircle size={14} className="text-[#FF5A00]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#FF5A00] uppercase">Código nuevo</span>
                    </div>
                    <p className="text-white/60 text-xs">Este GTIN no está registrado. Llena los datos y quedará ligado a tu tienda.</p>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Nombre del producto</label>
                      <input value={manualName} onChange={(e) => setManualName(e.target.value)}
                        placeholder="Ej. Pan Casero"
                        className="w-full mt-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/25 focus:border-[#FF5A00] focus:outline-none"
                        data-testid="scan-unknown-name" />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Presentación <span className="text-white/30 lowercase tracking-normal">(opc.)</span></label>
                      <input value={manualPresentation} onChange={(e) => setManualPresentation(e.target.value)}
                        placeholder="500 g / 1 pieza"
                        className="w-full mt-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/25 focus:border-[#FF5A00] focus:outline-none"
                        data-testid="scan-unknown-presentation" />
                    </div>
                    <PriceStockFields
                      salePrice={salePrice} setSalePrice={setSalePrice}
                      costPrice={costPrice} setCostPrice={setCostPrice}
                      stock={stock} setStock={setStock}
                    />
                  </div>
                  <button onClick={saveUnknown} disabled={saving || !manualName.trim() || !salePrice}
                    className="mt-4 w-full py-4 rounded-full bg-[#FF5A00] text-white text-xs font-mono uppercase tracking-widest hover:bg-white hover:text-black transition disabled:opacity-40 flex items-center justify-center gap-2"
                    data-testid="scan-save-unknown">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
                    Agregar a mi inventario
                  </button>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function PriceStockFields({ salePrice, setSalePrice, costPrice, setCostPrice, stock, setStock }) {
  const Cell = ({ label, val, setVal, testid, placeholder = "0.00" }) => (
    <div>
      <label className="font-mono text-[9px] tracking-widest text-white/40 uppercase">{label}</label>
      <div className="mt-1 flex items-center bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 focus-within:border-[#CCFF00]">
        <span className="text-white/40 mr-1 text-sm">$</span>
        <input value={val} onChange={(e) => setVal(e.target.value.replace(/[^0-9.]/g, ""))}
          placeholder={placeholder} inputMode="decimal"
          className="w-full bg-transparent text-white text-sm focus:outline-none placeholder-white/25"
          data-testid={testid} />
      </div>
    </div>
  );
  return (
    <div className="grid grid-cols-3 gap-2">
      <Cell label="Costo" val={costPrice} setVal={setCostPrice} testid="scan-cost" />
      <Cell label="Venta *" val={salePrice} setVal={setSalePrice} testid="scan-sale" />
      <div>
        <label className="font-mono text-[9px] tracking-widest text-white/40 uppercase">Stock</label>
        <input value={stock} onChange={(e) => setStock(e.target.value.replace(/[^0-9.]/g, ""))}
          placeholder="0" inputMode="decimal"
          className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-[#CCFF00] focus:outline-none placeholder-white/25"
          data-testid="scan-stock" />
      </div>
    </div>
  );
}
